import { useEffect } from "react";
import { injectPostgresEndpoints } from "../store/api/postgresApi";
import { useGetCurrentUserQuery } from "../store/api/postgresUserApi";
import { useAuth } from "../supabase/AuthProvider";

export type SurveyWave = "start" | "3m" | "6m" | "12m";
export type SurveyAnswers = Record<string, string | number | Array<string | number>>;
export interface SurveyQuestion {
  id: string;
  part: number;
  kind: "single" | "multi" | "rating" | "scenario" | "text";
  prompt: string;
  hint?: string;
  note?: string;
  placeholder?: string;
  options?: Array<{ value: string | number; label: string }>;
}
export interface SurveyDefinition {
  key: string;
  version: string;
  title: string;
  subtitle: string;
  frontMatter: string[];
  sourceScheduleText: string;
  sourcePrivacyText: string;
  sourceCarriedOverNote: string;
  sourceSubmitLabel: string;
  sourceThankYouHeading: string;
  sourceThankYouText: string;
  parts: Array<{id:number;title:string;note?:string;scaleKey?:string}>;
  questions: SurveyQuestion[];
  scenarioForms: Record<"sep" | "mid" | "end",SurveyQuestion[]>;
  waveForms: Record<SurveyWave,"sep" | "mid" | "end">;
}
export interface SurveyStatus {
  actorId: string;
  organisationId: string;
  enabled: boolean;
  observedAt: string;
  programmeYear: string;
  definitionKey: string;
  definitionVersion: string;
  baselineRequired: boolean;
  baselineSubmittedAt: string | null;
  clockStartedAt?: string | null;
  activatedAt: string | null;
  extraWaves: boolean;
  waves: Array<{
    wave: SurveyWave;
    eligible: boolean;
    required: boolean;
    dueDate: string | null;
    skipped?: boolean;
    responded: boolean;
    submittedAt: string | null;
    editableUntil: string | null;
    canEdit: boolean;
    dismissedUntil: string | null;
    bannerVisible: boolean;
  }>;
}
export interface OwnSurveyResponse {
  id:string;
  wave:SurveyWave;
  programme_year:string;
  submitted_at:string;
  updated_at:string;
  required:boolean;
  answers:SurveyAnswers;
}
export interface SurveyScope { actorId:string;organisationId:string }
const tag=(scope:SurveyScope)=>({type:"Users" as const,id:`SURVEY:${scope.actorId}:${scope.organisationId}`});
const surveyApi=injectPostgresEndpoints({
  endpoints:builder=>({
    getSurveyStatus:builder.query<SurveyStatus,SurveyScope>({
      query:()=>"/surveys/me/status",providesTags:(_result,_error,scope)=>[tag(scope)],keepUnusedDataFor:0,
    }),
    getSurveyDefinition:builder.query<SurveyDefinition,{key:string;version:string;scope:SurveyScope}>({
      query:({key,version})=>`/surveys/definitions/${encodeURIComponent(key)}/${encodeURIComponent(version)}`,
      keepUnusedDataFor:0,
    }),
    getOwnSurveyResponses:builder.query<OwnSurveyResponse[],SurveyScope>({
      query:()=>"/surveys/me/responses",providesTags:(_result,_error,scope)=>[tag(scope)],keepUnusedDataFor:0,
    }),
    getOwnSurveyExport:builder.query<OwnSurveyResponse[],SurveyScope>({
      query:()=>"/surveys/me/responses/export",keepUnusedDataFor:0,
    }),
    saveSurveyResponse:builder.mutation<OwnSurveyResponse,{scope:SurveyScope;wave:SurveyWave;answers:SurveyAnswers;edit:boolean}>({
      query:({wave,answers,edit})=>({url:edit?`/surveys/me/responses/${wave}`:"/surveys/me/responses",method:edit?"PUT":"POST",body:edit?{answers}:{wave,answers}}),
      invalidatesTags:(_result,_error,{scope})=>[tag(scope)],
    }),
    dismissSurvey:builder.mutation<{wave:SurveyWave;dismissedUntil:string},{scope:SurveyScope;wave:SurveyWave}>({
      query:({wave})=>({url:"/surveys/me/dismiss",method:"POST",body:{wave}}),
      invalidatesTags:(_result,_error,{scope})=>[tag(scope)],
    }),
  }),
});
export const {useGetSurveyDefinitionQuery,useGetOwnSurveyResponsesQuery,useLazyGetOwnSurveyExportQuery,useSaveSurveyResponseMutation,useDismissSurveyMutation}=surveyApi;

/** Query identity includes both person and school; stale scopes never open lessons. */
export function useSurveyStatus() {
  const {user}=useAuth();
  const profile=useGetCurrentUserQuery(undefined,{skip:!user,refetchOnMountOrArgChange:true});
  const scope:SurveyScope={actorId:user?.id??"",organisationId:profile.currentData?.organisation_id??""};
  const scoped=!!scope.actorId && !!scope.organisationId && profile.currentData?.id===scope.actorId;
  const query=surveyApi.useGetSurveyStatusQuery(scope,{skip:!scoped,refetchOnMountOrArgChange:true});
  const matches=query.currentData?.actorId===scope.actorId && query.currentData?.organisationId===scope.organisationId;
  const mismatch=!!query.currentData && !matches;
  const refreshProfile=profile.refetch;
  useEffect(()=>{if(mismatch) {void refreshProfile();}},[mismatch,refreshProfile]);
  return {
    status:scoped && matches && !query.isError && !query.isFetching && !profile.isError && !profile.isFetching?query.currentData:undefined,
    scope,
    loading:profile.isLoading || profile.isFetching || query.isFetching || mismatch,
    error:profile.isError || query.isError || (!profile.isLoading && !profile.isFetching && !scoped),
    refetch:async () => { await profile.refetch(); if(scoped) {await query.refetch();} },
  };
}
